import { Injectable } from '@angular/core';
import {
  EnvironmentVariables,
  LoggerService,
  StateService,
} from '@adreno-common';
import { defer, from, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { HousingServiceAgreements } from '../code/housing';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class WebPopService extends StateService<State> {
  private regex = /<input .* id="txtresult" .* value="(.*)" .* \/>/gim;
  private response$ = this.select((state) => state.response);
  request$ = this.select((state) => state.request);
  webTabs$ = this.select((state) => state.webTabs);

  constructor(
    private environment: EnvironmentVariables,
    private http: HttpClient,
    private toastr: ToastrService,
    private logger: LoggerService
  ) {
    super(initalState);
  }

  private buildUrl(
    url: string,
    additionalVars?: { [key: string]: string | undefined | null }
  ) {
    if (additionalVars) {
      const searchParams = new URLSearchParams(
        Object.entries(additionalVars || []).map(([ky, val]) => [ky, val || ''])
      );
      url = `${url}${url.includes('?') ? '&' : '?'}${searchParams}`;
    }
    return url;
  }

  api$(
    url: string,
    additionalVars?: { [key: string]: string | undefined | null }
  ) {
    url = this.buildUrl(url, additionalVars);
    if (url.toLowerCase().startsWith(this.environment.apiPath.toLowerCase())) {
      return this.http.get<HousingServiceAgreements>(url, additionalVars).pipe(
        map((data) => JSON.stringify(data)),
        catchError((err) => {
          this.logger.error('WebPopService', 'api$', err)();
          this.toastr.error(
            `Web API Error: ${err.message || err.statusText || err}`,
            'Error'
          );
          return from([`Error: ${err.message || err.statusText || err}`]);
        })
      );
    } else {
      return from(fetch(url)).pipe(
        switchMap((resp) => from(resp.text())),
        map((body) => {
          const m = this.regex.exec(body);
          if (m !== null) {
            const value = m[1];
            return value;
          } else {
            //could not find txtresult
            return body;
          }
        })
      );
    }
  }

  capture$(
    url: string,
    additionalVars?: { [key: string]: string | undefined | null }
  ) {
    url = this.buildUrl(url, additionalVars);
    return defer(() => {
      this.state = { request: url, response: undefined };
      return this.response$.pipe(
        filter((resp) => !!resp),
        take(1),
        map((resp) => resp!)
      );
    });
  }

  closeInternal() {
    this.state = { webTabs: [] };
  }

  external(
    url: string,
    additionalVars?: { [key: string]: string | undefined | null }
  ) {
    url = this.buildUrl(url, additionalVars);
    open(url, '_blank');
  }

  logResponse(result: any) {
    if (typeof result === 'string') {
      this.state = { request: undefined, response: result };
    } else {
      const strResult = JSON.stringify(result);
      this.state = { request: undefined, response: strResult };
    }
  }

  openInternal(
    url: string,
    title: string = 'Web',
    additionalVars?: { [key: string]: string | undefined | null }
  ) {
    url = this.buildUrl(url, additionalVars);
    const newTab = { url, title };
    let { webTabs } = this.state;
    const existing = webTabs.findIndex((tab) => tab.title == title);
    if (existing >= 0) {
      webTabs = [
        ...(webTabs?.slice(0, existing) || []),
        newTab,
        ...(webTabs?.slice(existing + 1) || []),
      ];
    } else {
      webTabs = [...webTabs, newTab];
    }
    this.state = { webTabs };
  }
}

interface State {
  request?: string;
  response?: string;
  webTabs: {
    title?: string;
    url?: string;
  }[];
}

const initalState: State = {
  request: undefined,
  response: undefined,
  webTabs: [],
};
